import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Twitter, Facebook, Linkedin, Link as LinkIcon } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blogs/${slug}`).then((r) => setBlog(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const url = typeof window !== "undefined" ? window.location.href : "";

  const share = (platform) => {
    const encoded = encodeURIComponent(url);
    const title = encodeURIComponent(blog?.title || "");
    const links = {
      twitter: `https://twitter.com/intent/tweet?url=${encoded}&text=${title}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`,
    };
    if (platform === "copy") {
      navigator.clipboard?.writeText(url);
      toast.success("Link copied");
      return;
    }
    window.open(links[platform], "_blank", "noopener,noreferrer");
  };

  if (notFound) return <div className="max-w-3xl mx-auto py-24 text-center text-white/60">Article not found.</div>;
  if (!blog) return <div className="max-w-3xl mx-auto py-24 text-center text-white/40">Loading…</div>;

  return (
    <article className="max-w-3xl mx-auto px-5 md:px-8 py-16" data-testid="blog-detail">
      <Link to="/blogs" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-mono uppercase tracking-widest mb-8">
        <ArrowLeft size={14} /> All Articles
      </Link>
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// {blog.category}</div>
      <h1 className="font-display text-4xl md:text-6xl font-black uppercase leading-none tracking-tighter">{blog.title}</h1>
      <div className="mt-6 flex items-center gap-4 text-sm text-white/50 font-mono">
        <span>By {blog.author}</span>
        <span>·</span>
        <span>{new Date(blog.published_at || blog.created_at).toLocaleDateString()}</span>
      </div>
      <img src={blog.cover_image} alt={blog.title} className="mt-10 w-full aspect-video object-cover border border-white/10" />

      <div className="mt-10 prose prose-invert max-w-none text-white/80 leading-relaxed whitespace-pre-wrap">
        {blog.content}
      </div>

      <div className="mt-12 pt-8 border-t border-white/10">
        <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-4">Share</div>
        <div className="flex gap-3">
          <button data-testid="share-twitter" onClick={() => share("twitter")} className="p-3 border border-white/20 hover:border-brand-orange hover:text-brand-orange"><Twitter size={16} /></button>
          <button data-testid="share-facebook" onClick={() => share("facebook")} className="p-3 border border-white/20 hover:border-brand-orange hover:text-brand-orange"><Facebook size={16} /></button>
          <button data-testid="share-linkedin" onClick={() => share("linkedin")} className="p-3 border border-white/20 hover:border-brand-orange hover:text-brand-orange"><Linkedin size={16} /></button>
          <button data-testid="share-copy" onClick={() => share("copy")} className="p-3 border border-white/20 hover:border-brand-blue hover:text-brand-blue"><LinkIcon size={16} /></button>
        </div>
      </div>
    </article>
  );
}
